import { ltjsModule, typeMap } from './ltjsEnvironment';
import { traceRetain, traceRelease, traceEnabled, logRetainReleaseTrace } from './util/TraceUtil';
import { insertWrapper, scheduleGarbageCollection, isToken, forAllObjectsInCloset } from './WrappableCloset';
import * as ClosetUtil from './util/ClosetUtil';
function getStaticFunction(Class, name) {
    if (Class.hasOwnProperty(name)) {
        var func = Class[name];
        if (typeof func === 'function') {
            return func;
        }
    }
    return undefined;
}
function getMemberFunction(object, name) {
    if (name in object) {
        var func = object[name];
        if (typeof func === 'function') {
            return func;
        }
    }
    return undefined;
}
function getSuffixFromArgs(args) {
    var suffix = '_';
    var numArgs = args.length;
    if (numArgs === 0) {
        // VOID
        suffix += 'v';
    }
    else {
        for (var i = 0; i < numArgs; i++) {
            var arg = args[i];
            if (arg === undefined) {
                suffix += 'o';
            }
            else {
                suffix += (typeof arg).charAt(0);
            }
        }
    }
    return suffix;
}
/**
 *
 */
var ClosetObject = /** @class */ (function () {
    function ClosetObject(tokenOrArgs, closetAddress, closetKey) {
        //
        // ClosetObjects are constructed as temporary objects.
        // They must be explicitly retained to avoid garbage collection.
        // _retainCount is a reference count.
        //
        this._retainCount = 0;
        //
        // closetAddress is the handle used to reference the c++ object in the closet.
        // 0 represents a nullptr, and is the initial value of _closetAddress.
        // Unless something unexpected happens, the initialization code below should
        // set closetAddress to a valid, non-zero value.
        //
        // After garbage collection (see WrapperCloset.js), the closet address is set
        // back to 0, making the object 'expired' and unusable. Attempts to call methods on an
        // expired wrapper will result in an error thrown.
        //
        this._closetAddress = 0;
        //
        // A ClosetObject can be explicitly created by calling the constructor.
        // When this occurs there are 0 or more normal arguments passed by the caller.
        // These arguments are then passed into the object's static _create method.
        // If there is no such _create method then that means that this class
        // is abstract and can not be instantiated.
        // The other way to get a ClosetObject is through implicit construction, which
        // happens when a wrapper is the return value of another function.  In this case
        // the wrapper is automatically created, and the ClosetObject.__TOKEN__ is passed
        // as the one and only parameter.
        //
        if (isToken(tokenOrArgs)) {
            //
            // Implicit creation of a wrapper for an already existing c++ object.
            //
            if (!closetAddress || !closetKey) {
                throw new Error('Object can not be instantiated');
            }
            this._closetAddress = closetAddress;
            //
            // Stuff this new object into the closet.
            //
            insertWrapper(this, closetKey);
        }
        else {
            //
            // Explicit creation.
            // Get the _create function that exists on this object's constructor (static function)
            // and call that function. It returns the closetKey, used to create the wrapper.
            //
            var createFunction = getStaticFunction(this.constructor, '_create');
            if (createFunction === undefined) {
                throw new Error('Object can not be instantiated');
            }
            //
            var createdClosetKey = createFunction.apply(this, arguments);
            //
            // Commit the staged object to the c++ closet.  This returns the closetAddress,
            // used to reference the object.
            //
            this._closetAddress = ltjsModule.commitStagedObject(createdClosetKey);
            //
            // Stuff this new object into the closet.
            //
            insertWrapper(this, createdClosetKey);
        }
    }
    ClosetObject.prototype.getClosetAddress = function () {
        return this._closetAddress;
    };
    /**
     * @private
     */
    ClosetObject.prototype._resetClosetAddress = function () {
        this._closetAddress = 0;
    };
    ClosetObject.prototype.isRetained = function () {
        return this._retainCount > 0;
    };
    /**
     * @deprecated
     */
    ClosetObject.prototype.isAutoReleased = function () {
        return !this.isRetained();
    };
    ClosetObject.prototype.getRetainCount = function () {
        return this._retainCount;
    };
    ClosetObject.prototype._getValidClosetAddress = function () {
        this._assertNotExpired();
        return this._closetAddress;
    };
    /**
     * @private
     */
    ClosetObject._getValidClosetAddress = function (closetObject) {
        return closetObject ? closetObject._getValidClosetAddress() : 0;
    };
    /**
     * @private
     */
    ClosetObject.prototype.isExpired = function () {
        return this._closetAddress === 0;
    };
    /**
     * @deprecated
     */
    ClosetObject.prototype.isNull = function () {
        return this.isExpired();
    };
    ClosetObject.prototype._assertNotExpired = function () {
        if (this.isExpired()) {
            throw new Error('This ClosetObject has gone out of scope and is no longer usable. ClosetObjects must be retained if they are to be used outside of local scope.');
        }
    };
    ClosetObject.prototype.equals = function (closetObject) {
        if (closetObject === null) {
            return false;
        }
        return ClosetObject.equals(this, closetObject);
    };
    ClosetObject.equals = function (objA, objB) {
        //
        // Trivially equal
        //
        if (objA === objB) {
            return true;
        }
        //
        // Let C++ decide (equals operator overloads)
        //
        if (objA instanceof ClosetObject && objB instanceof ClosetObject) {
            return ltjsModule.closetObjectsEqual(objA._getValidClosetAddress(), objB._getValidClosetAddress());
        }
        return false;
    };
    ClosetObject.prototype.getWrapperType = function () {
        if (!this.isExpired()) {
            // TODO: reconsider using typemap directly from ltjsModule
            // Also, typeEntry should be typed. Might suggest that we need a structured type for the TypeMap.
            var typeId = ltjsModule.getTypeId(this._closetAddress);
            var typeEntry = typeMap.getTypeEntry(typeId);
            if (typeEntry && typeEntry.length > 0) {
                return typeEntry[0];
            }
        }
        return null;
    };
    ClosetObject.prototype.retain = function () {
        //
        // cannot be expired
        //
        this._assertNotExpired();
        //
        // Increment the retainCount, thereby ensuring that the object will
        // not be removed from the closet until released.
        //
        ++this._retainCount;
        //
        // Keep track of trace information
        //
        if (traceEnabled) {
            traceRetain(this);
        }
        return this;
    };
    ClosetObject.prototype.release = function () {
        //
        // release decrements the retainCount of the closetObject
        // and schedules garbage collection if needed.
        //
        //
        // cannot be expired
        //
        this._assertNotExpired();
        //
        // must have been retained
        //
        if (this._retainCount === 0) {
            throw new Error('This ClosetObject is not retained and therefore cannot be released. You may have mismatched retain/release calls.');
        }
        //
        // decrement retain count
        //
        --this._retainCount;
        //
        // If retainCount reaches 0 then it is eligible for garbage collection.
        //
        if (this._retainCount === 0) {
            scheduleGarbageCollection();
        }
        //
        // Keep track of trace information
        //
        if (traceEnabled) {
            traceRelease(this);
        }
        return this;
    };
    ClosetObject.getClosetAddress = function (co) {
        return ClosetUtil.getClosetAddress(co);
    };
    ClosetObject.getAddress = function (co) {
        return ClosetUtil.getAddress(co);
    };
    ClosetObject.callOverloadedFunction = function (object, funcBaseName, args) {
        var func = undefined;
        var suffix = getSuffixFromArgs(args);
        if (suffix) {
            var objType = typeof object;
            if (objType === 'object') {
                func = getMemberFunction(object, funcBaseName + suffix);
            }
            else if (objType === 'function') {
                func = getStaticFunction(object, funcBaseName + suffix);
            }
        }
        if (!func) {
            throw new Error('Function ' + funcBaseName + ' not available for specified arguments');
        }
        return func.apply(object, args);
    };
    ClosetObject.logRetainReleaseTrace = function (closetObject) {
        logRetainReleaseTrace(closetObject);
    };
    ClosetObject.logAllRetainReleaseTraces = function () {
        forAllObjectsInCloset(function (closetObject) {
            logRetainReleaseTrace(closetObject);
        });
    };
    return ClosetObject;
}());
export default ClosetObject;
